import React, { useState, useEffect } from "react";

interface AddToWishlistProps {
  productId: string;
  className?: string;
  buttonText?: string;
  removeButtonText?: string;
  isInWishlist?: boolean;
}

export const AddToWishlist: React.FC<AddToWishlistProps> = ({
  productId,
  buttonText = "Favorit",
  removeButtonText = "Obriši Favorit",
  isInWishlist: initialIsInWishlist = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInWishlist, setIsInWishlist] = useState(initialIsInWishlist);

  // Update isInWishlist when the prop changes
  useEffect(() => {
    setIsInWishlist(initialIsInWishlist);
  }, [initialIsInWishlist]);

  const handleToggleWishlist = async () => {
    if (!productId) {
      setError("Product ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `/wishlist/artikl/${productId}?_format=json`;
      const method = isInWishlist ? "DELETE" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body:
          method === "PUT"
            ? JSON.stringify({ product_id: productId })
            : undefined,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isInWishlist ? "remove from" : "add to"} wishlist: ${
            response.status
          }`
        );
      }

      const data = await response.json();

      switch (data.result.toLowerCase()) {
        case "added": {
          setIsInWishlist(true);
          break;
        }
        case "removed": {
          setIsInWishlist(false);
          break;
        }
      }
    } catch (err) {
      console.error(
        `Error ${isInWishlist ? "removing from" : "adding to"} wishlist:`,
        err
      );
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isInWishlist ? "remove from" : "add to"} wishlist`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button class based on state
  const getButtonClass = () => {
    // const baseClass = "product-compare-remove";

    if (isInWishlist) {
      // When product is in wishlist - red background
      return `${'product-compare-remove'}`;
    } else {
      // When product is not in wishlist - blue background
      return `${'product-compare-add'}`;
    }
  };

  return (
    <div className="product-compare-remove-container">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleToggleWishlist();
        }}
        disabled={isLoading}
        className={getButtonClass()}
      >
        {isLoading ? (
          <span className="flex items-top">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {isInWishlist ? "Removing..." : "Adding..."}
          </span>
        ) : isInWishlist ? (
          removeButtonText
        ) : (
          buttonText
        )}
      </button>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};
